import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

/**
 * VehicleLookupPanel
 *
 * Self-contained UK vehicle reg / VIN lookup UI.
 * - Renders the yellow-plate styled input + lookup button
 * - Calls GET /api/vehicle-lookup?q=...
 * - Renders a merged result card (vehicle details + tax + MOT history)
 *
 * Used by both the standalone VehicleLookup page and the VehicleLookupModal.
 *
 * Props:
 *   isDark          (bool)
 *   autoFocus       (bool)  - focus the input on mount
 *   initialQuery    (string) - prefill + auto-search if non-empty
 *   compact         (bool)  - tighter padding for modal use
 *   onResult        (fn)    - called with merged result object on success
 *   onSelectVehicle (fn)    - extra action button "Use this vehicle" if provided
 */
export default function VehicleLookupPanel({
  isDark = false,
  autoFocus = true,
  initialQuery = '',
  compact = false,
  onResult,
  onSelectVehicle,
}) {
  const [query, setQuery] = useState(initialQuery || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [showRaw, setShowRaw] = useState(false);
  const inputRef = useRef(null);
  const didAutoSearchRef = useRef(false);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select?.();
    }
  }, [autoFocus]);

  useEffect(() => {
    if (!didAutoSearchRef.current && initialQuery && initialQuery.trim().length >= 2) {
      didAutoSearchRef.current = true;
      runLookup(initialQuery.trim());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runLookup = async (q) => {
    const term = String(q ?? query ?? '').trim();
    if (!term) {
      setError('Enter a registration or VIN.');
      return;
    }
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const { data } = await axios.get('/api/vehicle-lookup', { params: { q: term } });
      setResult(data);
      onResult?.(data);
    } catch (err) {
      if (err?.response?.status === 404) {
        setError('No vehicle found for that registration or VIN.');
      } else {
        setError(err?.response?.data?.error || err.message || 'Lookup failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    runLookup();
  };

  const colors = {
    text: isDark ? '#e2e8f0' : '#1a3c5e',
    subText: isDark ? '#94a3b8' : '#5a7184',
    card: isDark ? '#1e293b' : '#ffffff',
    cardBorder: isDark ? '#334155' : '#d6e2ed',
    sectionBorder: isDark ? '#334155' : '#e3ecf5',
    chipBg: isDark ? '#0f172a' : '#eef5ff',
    chipBorder: isDark ? '#334155' : '#cbd9ec',
    okBg: isDark ? '#064e3b' : '#dcfce7',
    okText: isDark ? '#bbf7d0' : '#166534',
    warnBg: isDark ? '#7c2d12' : '#fee2e2',
    warnText: isDark ? '#fecaca' : '#991b1b',
    neutralBg: isDark ? '#1f2937' : '#f1f5f9',
    neutralText: isDark ? '#cbd5e1' : '#334155',
  };

  const padOuter = compact ? '0.5rem' : '1rem';

  return (
    <div style={{ padding: padOuter, color: colors.text, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Plate-styled search box */}
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
        <div style={ukPlateOuterStyle}>
          <div style={ukPlateBlueStyle}>
            <div style={{ fontSize: 12, lineHeight: 1, fontWeight: 700 }}>UK</div>
          </div>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value.toUpperCase())}
            placeholder="AB12 CDE"
            maxLength={20}
            spellCheck={false}
            autoComplete="off"
            style={ukPlateInputStyle}
            aria-label="Vehicle registration or VIN"
          />
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
          <button
            type="submit"
            disabled={loading || !query.trim()}
            style={{
              padding: '0.6rem 1.4rem',
              fontSize: '1rem',
              fontWeight: 600,
              borderRadius: 6,
              border: 'none',
              cursor: loading ? 'wait' : 'pointer',
              background: loading ? '#94a3b8' : '#1e40af',
              color: '#fff',
              minWidth: 140,
            }}
          >
            {loading ? 'Looking up…' : 'Lookup'}
          </button>
          {query && (
            <button
              type="button"
              onClick={() => { setQuery(''); setResult(null); setError(''); inputRef.current?.focus(); }}
              style={{ padding: '0.6rem 1rem', fontSize: '0.9rem', borderRadius: 6, border: `1px solid ${colors.cardBorder}`, background: 'transparent', color: colors.text, cursor: 'pointer' }}
            >
              Clear
            </button>
          )}
        </div>
        <div style={{ fontSize: 12, color: colors.subText, textAlign: 'center' }}>
          Enter a UK registration (e.g. <strong>AB12 CDE</strong>) or a 17-character VIN.
        </div>
      </form>

      {error && (
        <div role="alert" style={{
          padding: '0.75rem 1rem',
          background: colors.warnBg,
          color: colors.warnText,
          border: `1px solid ${colors.warnText}`,
          borderRadius: 8,
          fontWeight: 500,
        }}>
          {error}
        </div>
      )}

      {result && (
        <div style={{
          background: colors.card,
          border: `1px solid ${colors.cardBorder}`,
          borderRadius: 10,
          padding: compact ? '0.75rem' : '1rem 1.25rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
        }}>
          <ResultHeader result={result} colors={colors} onSelectVehicle={onSelectVehicle} />
          <DetailsGrid result={result} colors={colors} />
          {Array.isArray(result.motHistory) && result.motHistory.length > 0 && (
            <MotHistorySection history={result.motHistory} colors={colors} />
          )}
          <div>
            <button
              type="button"
              onClick={() => setShowRaw((v) => !v)}
              style={{ background: 'transparent', color: colors.subText, border: 'none', cursor: 'pointer', padding: 0, fontSize: 12, textDecoration: 'underline' }}
            >
              {showRaw ? 'Hide raw API response' : 'Show raw API response'}
            </button>
            {showRaw && (
              <pre style={{
                marginTop: 8,
                padding: '0.75rem',
                background: colors.neutralBg,
                color: colors.neutralText,
                borderRadius: 6,
                overflow: 'auto',
                maxHeight: 320,
                fontSize: 11,
              }}>{JSON.stringify(result.raw, null, 2)}</pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ResultHeader({ result, colors, onSelectVehicle }) {
  const reg = result.registration || result.queryNormalized || '—';
  const titleParts = [result.make, result.model].filter(Boolean);
  const title = titleParts.join(' ') || 'Vehicle';

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ ...miniPlateStyle }}>{formatPlateDisplay(reg)}</div>
        <h3 style={{ margin: 0, fontSize: '1.25rem', color: colors.text }}>{title}</h3>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <StatusChip label="Tax" value={result.taxStatus} colors={colors} />
          <StatusChip label="MOT" value={result.motStatus} colors={colors} />
          {result.vesOnly && <Chip text="VES only (no MOT record)" colors={colors} tone="neutral" />}
          {result.sources?.mot && !result.vesOnly && <Chip text="MOT + DVLA" colors={colors} tone="neutral" />}
          {result.queryType === 'VIN' && <Chip text="Looked up by VIN" colors={colors} tone="neutral" />}
        </div>
      </div>
      {onSelectVehicle && (
        <button
          type="button"
          onClick={() => onSelectVehicle(result)}
          style={{
            padding: '0.6rem 1rem',
            fontSize: '0.9rem',
            fontWeight: 600,
            borderRadius: 6,
            border: 'none',
            background: '#0d9488',
            color: '#fff',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          Use this vehicle
        </button>
      )}
    </div>
  );
}

function DetailsGrid({ result, colors }) {
  const items = [
    ['Make', result.make],
    ['Model', result.model],
    ['Colour', result.primaryColour],
    ['Fuel', result.fuelType],
    ['Engine size', result.engineSize ? `${result.engineSize} cc` : null],
    ['Year', result.yearOfManufacture || (result.manufactureDate ? new Date(result.manufactureDate).getFullYear() : null)],
    ['First used', formatDate(result.firstUsedDate)],
    ['Registration date', formatDate(result.registrationDate)],
    ['First registered', result.monthOfFirstRegistration || null],
    ['First DVLA reg.', result.monthOfFirstDvlaRegistration || null],
    ['Tax status', result.taxStatus],
    ['Tax due', formatDate(result.taxDueDate)],
    ['Additional Rate Tax ends', formatDate(result.artEndDate)],
    ['MOT status', result.motStatus],
    ['MOT expiry', formatDate(result.motExpiryDate)],
    ['Last MOT mileage', result.lastMotMileage != null ? `${result.lastMotMileage.toLocaleString()} ${result.lastMotMileageUnit || ''}`.trim() : null],
    ['CO₂', result.co2Emissions != null ? `${result.co2Emissions} g/km` : null],
    ['Euro status', result.euroStatus],
    ['Type approval', result.typeApproval],
    ['Wheelplan', result.wheelplan],
    ['Revenue weight', result.revenueWeight != null ? `${result.revenueWeight} kg` : null],
    ['V5C issued', formatDate(result.dateOfLastV5CIssued)],
    ['Marked for export', result.markedForExport == null ? null : (result.markedForExport ? 'Yes' : 'No')],
    ['Outstanding recall', result.hasOutstandingRecall || null],
  ].filter(([, v]) => v !== null && v !== undefined && v !== '');

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
      gap: '0.6rem 1rem',
    }}>
      {items.map(([label, value]) => (
        <div key={label} style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <div style={{ fontSize: 11, color: colors.subText, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
          <div style={{ fontSize: 14, color: colors.text, fontWeight: 500, wordBreak: 'break-word' }}>{String(value)}</div>
        </div>
      ))}
    </div>
  );
}

function MotHistorySection({ history, colors }) {
  return (
    <div style={{ borderTop: `1px solid ${colors.sectionBorder}`, paddingTop: '0.75rem' }}>
      <h4 style={{ margin: '0 0 0.5rem 0', fontSize: 14, color: colors.text }}>
        MOT history ({history.length} {history.length === 1 ? 'test' : 'tests'})
      </h4>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: 360, overflow: 'auto' }}>
        {history.map((t, i) => {
          const pass = (t.testResult || '').toUpperCase() === 'PASSED' || (t.testResult || '').toUpperCase() === 'PASS';
          return (
            <div key={i} style={{
              border: `1px solid ${colors.sectionBorder}`,
              borderLeft: `4px solid ${pass ? '#16a34a' : '#dc2626'}`,
              borderRadius: 6,
              padding: '0.5rem 0.75rem',
              background: colors.neutralBg,
            }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'space-between', alignItems: 'baseline' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: colors.text }}>
                  {formatDate(t.completedDate)} — {t.testResult || 'Unknown'}
                </div>
                <div style={{ fontSize: 12, color: colors.subText }}>
                  {t.odometerResultType && t.odometerResultType !== 'READ'
                    ? `Mileage not recorded (${t.odometerResultType})`
                    : (t.odometerValue != null ? `${t.odometerValue.toLocaleString()} ${t.odometerUnit || ''}` : 'No mileage')}
                  {t.expiryDate ? ` · expires ${formatDate(t.expiryDate)}` : ''}
                  {t.motTestNumber ? ` · #${t.motTestNumber}` : ''}
                </div>
              </div>
              {Array.isArray(t.defects) && t.defects.length > 0 && (
                <ul style={{ margin: '0.5rem 0 0 1rem', padding: 0, fontSize: 12, color: colors.text }}>
                  {t.defects.map((d, j) => (
                    <li key={j} style={{ marginBottom: 2, color: d.dangerous ? '#dc2626' : colors.text }}>
                      <strong>{d.type || 'Note'}:</strong> {d.text}
                      {d.dangerous ? ' ⚠ Dangerous' : ''}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatusChip({ label, value, colors }) {
  if (!value) return null;
  const v = String(value).toLowerCase();
  let tone = 'neutral';
  if (['valid', 'taxed', 'passed', 'pass'].some((k) => v.includes(k))) tone = 'ok';
  else if (['expired', 'sorn', 'untaxed', 'failed', 'fail', 'not taxed'].some((k) => v.includes(k))) tone = 'warn';
  return <Chip text={`${label}: ${value}`} colors={colors} tone={tone} />;
}

function Chip({ text, colors, tone = 'neutral' }) {
  const palette = {
    ok: { bg: colors.okBg, color: colors.okText, border: colors.okText },
    warn: { bg: colors.warnBg, color: colors.warnText, border: colors.warnText },
    neutral: { bg: colors.chipBg, color: colors.neutralText, border: colors.chipBorder },
  }[tone];
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 600,
      background: palette.bg,
      color: palette.color,
      border: `1px solid ${palette.border}`,
    }}>{text}</span>
  );
}

function formatDate(s) {
  if (!s) return null;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' });
}

function formatPlateDisplay(reg) {
  if (!reg) return '';
  const clean = String(reg).replace(/\s+/g, '').toUpperCase();
  // Standard new-style UK plate AA00AAA → "AA00 AAA"
  if (/^[A-Z]{2}\d{2}[A-Z]{3}$/.test(clean)) {
    return `${clean.slice(0, 4)} ${clean.slice(4)}`;
  }
  return clean;
}

// --- styles ----------------------------------------------------------------
// We keep the plate look in inline styles so it renders identically in light + dark.
const ukPlateOuterStyle = {
  display: 'flex',
  alignItems: 'stretch',
  background: '#fdd80a',
  border: '2px solid #1a1a1a',
  borderRadius: 8,
  boxShadow: '0 4px 14px rgba(0,0,0,0.18), inset 0 0 0 1px rgba(0,0,0,0.06)',
  overflow: 'hidden',
  fontFamily: '"Charles Wright", "UKNumberPlate", "Arial Narrow", Arial, sans-serif',
};
const ukPlateBlueStyle = {
  background: '#1a3aa8',
  color: '#fdd80a',
  padding: '0 10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 32,
};
const ukPlateInputStyle = {
  border: 'none',
  outline: 'none',
  background: '#fdd80a',
  color: '#000',
  fontSize: 'clamp(1.6rem, 4vw, 2.4rem)',
  fontWeight: 700,
  letterSpacing: '0.12em',
  padding: '0.4rem 1rem',
  width: 'min(420px, 70vw)',
  textTransform: 'uppercase',
  textAlign: 'center',
};
const miniPlateStyle = {
  display: 'inline-block',
  background: '#fdd80a',
  color: '#000',
  border: '1px solid #1a1a1a',
  borderRadius: 4,
  padding: '2px 10px',
  fontSize: 16,
  fontWeight: 700,
  letterSpacing: '0.1em',
  fontFamily: '"Charles Wright", "UKNumberPlate", "Arial Narrow", Arial, sans-serif',
  alignSelf: 'flex-start',
};
